/**
 * Auth module — httpOnly-cookie session.
 *
 * SECURITY: The access + refresh tokens live in httpOnly cookies set by the
 * server (`nf_token`, `nf_refresh`). They are NOT accessible to JS, which
 * defeats the XSS token-theft vector flagged by the scanner. Every fetch in
 * this app must include credentials so the browser attaches those cookies.
 *
 * The previous version stored tokens in localStorage — that has been removed.
 * Do NOT reintroduce localStorage reads/writes for `nf_token` or `nf_refresh_token`.
 */

import { identifyUser, resetAnalytics } from './posthog';

let refreshTimer = null;
let isRefreshing = false;
let refreshPromise = null;
let userCache = null;
let initPromise = null;

const authClient = {
  authenticated: false,
  tokenParsed: null,
  login: () => { window.location.href = '/login'; },
  logout: () => { window.location.href = '/'; },
  updateToken: () => refreshToken(),
};

/**
 * Fetch /api/me to learn who the cookie-bearing session belongs to.
 * Populates the synchronous user cache so getUser()/isAuthenticated() work.
 * Returns the cached user or null.
 */
export const initAuth = () => {
  if (initPromise) return initPromise;
  initPromise = fetch('/api/me', { credentials: 'include' })
    .then((res) => {
      if (res.status === 401) return null;
      if (!res.ok) return null;
      return res.json();
    })
    .then((data) => {
      if (!data) { userCache = null; return null; }
      userCache = {
        id: data.id,
        email: data.email,
        firstName: data.email,
        name: data.email,
        roles: data.roles || ['default'],
        expiresAt: data.expires_at || 0,
      };
      authClient.authenticated = true;
      identifyUser(userCache); // tie analytics + replay to the logged-in user
      scheduleRefresh();
      return userCache;
    })
    .catch(() => { userCache = null; return null; })
    .finally(() => { /* keep the cache; reset initPromise only on logout */ });
  return initPromise;
};

/** Force a re-fetch of /api/me on next initAuth() call. Use after login/logout. */
export const resetAuthInit = () => { initPromise = null; };

export const getUser = () => userCache;

export const hasRole = (role) => !!(userCache && userCache.roles.includes(role));

export const isAuthenticated = () => !!userCache;

export const login = () => { window.location.href = '/login'; };

export const logout = () => {
  clearRefreshTimer();
  return fetch('/auth0/logout', { method: 'POST', credentials: 'include' })
    .catch(() => {})
    .finally(() => {
      userCache = null;
      initPromise = null;
      authClient.authenticated = false;
      resetAnalytics(); // clear identity so the next visitor is a fresh anon person
      window.location.href = '/';
    });
};

/**
 * Refresh the access token. Cookie carries the refresh credential, so the
 * request body is empty. The server rotates the cookie pair on success.
 */
export const refreshToken = () => {
  if (isRefreshing && refreshPromise) return refreshPromise;

  isRefreshing = true;
  refreshPromise = fetch('/auth0/refresh', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  })
    .then((response) => {
      if (!response.ok) {
        const error = new Error('Refresh failed');
        error.status = response.status;
        throw error;
      }
      return response.json();
    })
    .then((data) => {
      if (userCache && data.expires_at) userCache.expiresAt = data.expires_at;
      scheduleRefresh();
      return data;
    })
    .catch((err) => {
      // Only kick the user out when the refresh credential itself is invalid.
      if (err.status === 400 || err.status === 401 || err.status === 403) {
        userCache = null;
        initPromise = null;
        authClient.authenticated = false;
        window.location.replace('/login');
      }
      throw err;
    })
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });

  return refreshPromise;
};

/** Schedule a proactive refresh 2 minutes before the access token expires. */
export const scheduleRefresh = () => {
  clearRefreshTimer();
  if (!userCache || !userCache.expiresAt) return;
  const ttlMs = userCache.expiresAt - Date.now();
  if (ttlMs <= 0) return;
  const refreshInMs = Math.max(ttlMs - 120 * 1000, 10 * 1000);
  refreshTimer = setTimeout(() => { refreshToken().catch(() => {}); }, refreshInMs);
};

const clearRefreshTimer = () => {
  if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
};

/**
 * Fetch wrapper that:
 *   - sends cookies (credentials: 'include') so the httpOnly nf_token rides along
 *   - on 401, transparently calls /auth0/refresh and replays the request once
 */
export const authFetch = async (url, options = {}) => {
  // skipAuthRedirect: for PUBLIC pages (e.g. LinkTime guest join/chat) — still send the cookie so a
  // logged-in owner is recognized, but on 401 just return the response instead of running the
  // refresh→/login redirect, which would bounce an unauthenticated guest off the page.
  const { skipAuthRedirect, ...rest } = options;
  const opts = { ...rest, credentials: 'include' };
  let response = await fetch(url, opts);

  if (response.status === 401 && !skipAuthRedirect) {
    try {
      await refreshToken();
      response = await fetch(url, opts);
    } catch (e) {
      throw e;
    }
  }
  return response;
};

export const createApiClient = () => {
  const axios = require('axios');
  const apiClient = axios.create({
    baseURL: '/api',
    timeout: 10000,
    withCredentials: true,
  });

  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          await refreshToken();
          originalRequest.withCredentials = true;
          return apiClient(originalRequest);
        } catch (refreshErr) {
          return Promise.reject(refreshErr);
        }
      }
      return Promise.reject(error);
    }
  );

  return apiClient;
};

export default authClient;
